import {useMutation, UseMutationResult, useQuery, UseQueryResult} from '@tanstack/react-query';
import {z as r} from 'zod';

import {useLocale} from '../../provider/locale/locale-hook';
import {useSnackbar} from '../../provider/snackbar/snackbar-hook';
import {fetchAndDeserialize, getUrlParameters, postAndDeserialize} from '../../util/api-adapter';
import {getMessageFromReasonError} from '../../util/error';

const postReviewAbuseSchema = r.object({
    reason: r.string(),
});

type PostAbuseMutateType = {
    choices: Array<string>;
    description: string;
    reviewId: number;
};

type PostReviewAbuseType = r.infer<typeof postReviewAbuseSchema>;

function postReviewAbuse(reviewId: number, reason: string): Promise<PostReviewAbuseType> {
    return postAndDeserialize(`/v4/reviews/${reviewId}/abuse/`, postReviewAbuseSchema, {
        reason,
    });
}

// The exact `reason` string the backend receives: its length is what catalog limits are checked against.
export function getAbuseReasonText(choices: Array<string>, description: string): string {
    const choiceText = choices.length > 0 ? `${choices.join('. ')}.` : '';
    const trimmedDescription = description.trim();

    if (!choiceText) {
        return trimmedDescription;
    }

    return trimmedDescription ? `${choiceText} ${trimmedDescription}` : choiceText;
}

// Counts code points, as the backend's `len()` does, so emoji are not counted twice.
export function getAbuseReasonLength(choices: Array<string>, description: string): number {
    return [...getAbuseReasonText(choices, description)].length;
}

type UseSendAbuseMutationOptionsType = {
    suppressSnackbars?: boolean;
    suppressErrors?: boolean;
};

export function useSendAbuseMutation(
    options?: UseSendAbuseMutationOptionsType
): UseMutationResult<PostReviewAbuseType, unknown, PostAbuseMutateType> {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();
    const suppressSnackbars = options?.suppressSnackbars ?? false;
    const suppressErrors = options?.suppressErrors ?? false;

    return useMutation({
        mutationFn: (values) =>
            postReviewAbuse(values.reviewId, getAbuseReasonText(values.choices, values.description.toString())),
        onSuccess: () => {
            if (!suppressSnackbars) {
                snackbar.success({
                    description: getLocalizedString('REVIEWS__COMPLAINT__COMPLAINT_HAS_BEEN_SENT__DESCRIPTION'),
                    message: getLocalizedString('REVIEWS__COMPLAINT__COMPLAINT_HAS_BEEN_SENT'),
                });
            }
        },
        onError: (error) => {
            if (!suppressErrors) {
                snackbar.error(getMessageFromReasonError(error) || getLocalizedString('ERROR__SOMETHING_WENT_WRONG'));
            }
        },
    });
}

export enum AbuseReasonStatusEnum {
    Sent = 'sent',
    Accepted = 'accepted',
    Declined = 'declined',
    AutoSent = 'auto_sent',
    WillBeCheckedByAi = 'will_be_checked_by_ai',
    Default = 'default',
}

const reviewsAbuseReasonSchema = r.array(
    r.object({
        id: r.number(),
        status: r.nativeEnum(AbuseReasonStatusEnum),
    })
);

type ReviewsAbuseReasonType = r.infer<typeof reviewsAbuseReasonSchema>;

function getReviewsAbuseReasonUrl(reviewsId: Array<number>): string {
    return `/v4/reviews/abuse_status/${getUrlParameters({reviewsId})}`;
}

async function fetchReviewsAbuseReason(reviewIds: Array<number>): Promise<ReviewsAbuseReasonType> {
    return fetchAndDeserialize(getReviewsAbuseReasonUrl(reviewIds), reviewsAbuseReasonSchema);
}

export function useReviewsAbuseReason(reviewIds: Array<number>): UseQueryResult<ReviewsAbuseReasonType> {
    return useQuery([getReviewsAbuseReasonUrl(reviewIds)], () => fetchReviewsAbuseReason(reviewIds), {
        enabled: reviewIds.length > 0,
        refetchOnWindowFocus: false,
    });
}
